import { useQuery } from '@tanstack/react-query'
import { supabase } from '../../api'

const getOrders = async (userId) => {
  // Early return if userId is invalid
  if (!userId || userId === 'undefined') {
    return [];
  }

  // Get all pending orders
  const { data: pendingOrders, error: orderError } = await supabase
      .from('orders')
      .select('id')
      .eq('created_by', userId)
      .eq('status', 'Pending')

  if (orderError) {
    throw new Error(`Error fetching orders: ${orderError.message}`);
  }

  if (!pendingOrders || pendingOrders.length === 0) {
    return [];
  }

  const orderIds = pendingOrders.map(order => order.id);

  // Get items for all pending orders where product is not null
  const { data: orderItems, error: orderItemsError } = await supabase
      .from('order_items')
      .select('*, product(*), service(*, service_pricing_duration(*))')
      .in('order_id', orderIds)
      .not('product', 'is', null);

  if (orderItemsError) {
    throw new Error(`Error fetching order items: ${orderItemsError.message}`);
  }

  // Group items by product ID and count them
  const groupedItems = (orderItems || []).reduce((acc, item) => {
    const productId = item.product?.id;
    if (!acc[productId]) {
      acc[productId] = {
        ...item,
        count: 1
      };
    } else {
      acc[productId].count++;
    }
    return acc;
  }, {});

  return Object.values(groupedItems);
};

export default function useGetOrdersInTheCart(userId) {
  return useQuery({
    queryKey: ['useGetOrdersInTheCart', userId],
    queryFn: () => getOrders(userId),
    staleTime: 30000,              // ✅ Data fresh for 30 seconds
    cacheTime: 5 * 60 * 1000,      // ✅ Keep in cache for 5 minutes (for React Query v4)
    refetchOnWindowFocus: false,   // ✅ Don't refetch when switching tabs
    enabled: !!userId && userId !== 'undefined'
  })
}